import { IconButton, Stack, Text, useDisclosure, useToast } from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import { useState } from 'react';
import { HiOutlineArrowDownTray, HiOutlineDocument, HiOutlineEye } from 'react-icons/hi2';

import { trpc } from '../../config/trpc';
import { AttachmentPreviewModal, isPreviewable } from './AttachmentPreviewModal';
import { Draft } from './types';

type Props = {
  document: Draft['documents'][number];
};

/**
 * One attachment the draft kept: the same signed-URL download the request's Documents panel uses, and for an image or
 * PDF a preview in place — what the model read, beside what it extracted.
 */
export const AttachmentRow = ({ document }: Props) => {
  // The preview has its own mutation so the download button does not spin while a preview loads.
  const download = trpc.downloadDocument.useMutation();
  const inline = trpc.downloadDocument.useMutation();
  const toast = useToast({ position: 'bottom', duration: 4000 });
  const preview = useDisclosure();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const showPreview = isPreviewable(document.original_filename);

  const handleDownload = async () => {
    try {
      const url = await download.mutateAsync({ id: document.id });
      window.open(url, '_blank');
    } catch (error) {
      toast({ title: 'Error', description: error?.message, status: 'error' });
    }
  };

  const handlePreview = async () => {
    setPreviewUrl(null);
    preview.onOpen();
    try {
      const url = await inline.mutateAsync({ id: document.id, isInline: true });
      setPreviewUrl(url);
    } catch (error) {
      preview.onClose();
      toast({ title: 'Error', description: error?.message, status: 'error' });
    }
  };

  return (
    <Stack direction="row" align="center" spacing="2">
      <HiOutlineDocument />
      <Text fontSize="sm" noOfLines={1} flex="1" title={document.original_filename}>
        {document.original_filename}
      </Text>
      <Show if={showPreview}>
        <IconButton
          icon={<HiOutlineEye />}
          aria-label={`Preview ${document.original_filename}`}
          variant="outline"
          size="xs"
          onClick={handlePreview}
        />
        <AttachmentPreviewModal
          filename={document.original_filename}
          url={previewUrl}
          isOpen={preview.isOpen}
          onClose={preview.onClose}
        />
      </Show>
      <IconButton
        icon={<HiOutlineArrowDownTray />}
        aria-label={`Download ${document.original_filename}`}
        variant="outline"
        size="xs"
        isLoading={download.isLoading}
        onClick={handleDownload}
      />
    </Stack>
  );
};
